import { QuantumAppEnvNameType } from '../../contexts/types/quantum';
import { IEnvConfig } from '../envConfig/envConfig.types';
import { AppEnv, PiTargetBg, IAppConfigApiHosts } from './appConfig.types';

/**
 * Converts app env to its corresponding enum value
 */
export const toAppEnvEnum = (enumValue: string): AppEnv => {
  const keys = Object.keys(AppEnv).filter(
    (x) =>
      AppEnv[x as keyof typeof AppEnv].toLowerCase() === enumValue.toLowerCase()
  );
  const key = keys.length > 0 ? keys[0] : AppEnv.Mock;
  return AppEnv[key as keyof typeof AppEnv];
};

/**
 * Attempts to determine the environment by URL.
 */
export const getAppEnvByUrl = (): AppEnv => {
  const host = window.location.hostname.toLowerCase();

  if (
    host === 'dexter.spectrumtoolbox.com' ||
    host === 'blue.dexter.spectrumtoolbox.com' ||
    host === 'green.dexter.spectrumtoolbox.com'
  ) {
    return AppEnv.Prod;
  }

  if (host.includes('-engprod.')) {
    return AppEnv.EngProd;
  }

  if (host.includes('-uat.')) {
    return AppEnv.Uat;
  }

  if (host.includes('-qa.')) {
    return AppEnv.QA;
  }

  if (host.includes('-dev.')) {
    return AppEnv.Dev;
  }

  return AppEnv.Mock;
};

/**
 * Get the current app environment based on the URL
 */
export const getAppEnv = (envConfig?: IEnvConfig): AppEnv => {
  // Used for Local Development
  if (import.meta.env.VITE_APP_APP_ENV) {
    return toAppEnvEnum(import.meta.env.VITE_APP_APP_ENV as string);
  }

  // All Apps being deployed via PDE pipeline will use this var to determine env.
  if (envConfig && envConfig['app-env']) {
    return toAppEnvEnum(envConfig['app-env']);
  }

  // This is only used as a fall-back if for some reason the above logic falls thru.
  return getAppEnvByUrl();
};

/**
 * Gets the version name (ie: branch name)
 */
export const getAppVersionName = (envConfig: IEnvConfig): string => {
  return envConfig.ci_commit_ref_slug;
};

/**
 * Get the app full version by combining the Gitlab branch (or tag) name and the
 * pipeline ID it was deployed with.
 */
export const getAppPackageVersion = (envConfig: IEnvConfig): string => {
  const name = getAppVersionName(envConfig);
  const buildId = Number(envConfig.ci_pipeline_id);

  return name + (buildId ? `_${buildId}` : '');
};

/**
 * Tell Pi which BE endpoint to route to.
 */
export const getTargetBg = (): PiTargetBg => {
  const hostName = window.location.hostname.toLowerCase();
  const appEnv = getAppEnv();
  if (hostName.startsWith('blue.')) {
    return PiTargetBg.Blue;
  }

  if (hostName.startsWith('green.')) {
    return PiTargetBg.Green;
  }

  switch (appEnv) {
    case AppEnv.Uat:
      return PiTargetBg.UAT;
    case AppEnv.EngProd:
      return PiTargetBg.EngProd;
    case AppEnv.QA:
      return PiTargetBg.QA;
    default:
      return PiTargetBg.Default;
  }
};

/**
 * Generate a fake mock url to use for the path handlers that
 * the Mock Service will intercept.
 * Note: These are fake urls.
 * @param appShortName The app short name you want to build for.
 */
export const genMockHost = (appShortName: string): string =>
  `https://mock-${appShortName}.spectrumenterprise.net`;

/**
 * Generate mock endpoints for all API hosts
 * NOTE: Not a real url domain, just a placeholder that will be intercepted
 * by the Mock Service Worker.
 */
export const mockEndpoints = (): IAppConfigApiHosts => ({
  piHost: genMockHost('dexter'),
  directHost: genMockHost('dexter'),
});

/**
 * Get the API Host name to use for API calls
 * @param appEnv current app env
 */
export const getApiHost = (appEnv: AppEnv): IAppConfigApiHosts => {
  const PINXT_HOST = {
    prod: 'https://apis.spectrum.net',
    uat: 'https://apis.shared.uat-spectrum.net',
    qa: 'https://apis.shared.qa-spectrum.net', // @deprecated
    dev: 'https://apis.shared.dev-spectrum.net',
  };

  switch (appEnv) {
    case AppEnv.Prod:
      return {
        piHost: PINXT_HOST.prod,
        directHost: window.location.origin,
      };

    case AppEnv.EngProd:
      return {
        piHost: import.meta.env.VITE_APP_AUTH_HOST_ENGPROD || PINXT_HOST.uat,
        directHost:
          import.meta.env.VITE_APP_DIRECT_HOST_ENGPROD ||
          window.location.origin,
      };

    /** Currently no UAT Env, setup here if needed  */
    case AppEnv.Uat:
      return {
        piHost: import.meta.env.VITE_APP_AUTH_HOST_UAT || PINXT_HOST.uat,
        directHost:
          import.meta.env.VITE_APP_DIRECT_HOST_UAT || window.location.origin,
      };

    /** Currently no QA Env, setup here if needed  */
    case AppEnv.QA:
      return {
        piHost: import.meta.env.VITE_APP_AUTH_HOST_QA || PINXT_HOST.uat,
        directHost:
          import.meta.env.VITE_APP_DIRECT_HOST_QA || window.location.origin,
      };

    case AppEnv.Dev:
      return {
        piHost: import.meta.env.VITE_APP_AUTH_HOST_DEV || PINXT_HOST.uat,
        directHost:
          import.meta.env.VITE_APP_DIRECT_HOST_DEV || window.location.origin,
      };

    default:
      return mockEndpoints();
  }
};

/**
 * Get the full graphQL endpoint url
 * @param apiHost the current host
 * @param useDirectHost whether or not to use the direct host
 */
export const getGraphQlEndpoint = (
  apiHost: string,
  useDirectHost = false
): string => {
  if (useDirectHost) {
    return `${apiHost}/graphql`;
  }

  return `${apiHost}/entservices-graphql-p2/graphql`;
};

/**
 * Get the full REST endpoint url
 * @param apiHost the current host
 * @param useDirectHost whether or not to use the direct host
 */
export const getRestEndpoint = (
  apiHost: string,
  useDirectHost = false
): string => {
  if (useDirectHost) {
    return apiHost;
  }

  return `${apiHost}/entservices`;
};

/**
 * Get the correct device type name of the tdcs config to use.
 * @param appName current app name
 * @param appEnv current app env
 */
export const getTdcsDeviceType = (appName: string, appEnv: AppEnv): string => {
  switch (appEnv) {
    case AppEnv.QA:
      return `CLIENT-${appName}-QA`.toUpperCase();
    case AppEnv.EngProd:
      return `CLIENT-${appName}-ENGPROD`.toUpperCase();
    case AppEnv.Uat:
      return `CLIENT-${appName}-UAT`.toUpperCase();
    case AppEnv.Prod:
      return `CLIENT-${appName}-PROD`.toUpperCase();
    default:
      return `CLIENT-${appName}`.toUpperCase();
  }
};

/**
 * Whether or not the app environment is a prod based environment.
 * @param appEnv current app environment
 */
export const isProd = (appEnv: AppEnv): boolean => {
  return [AppEnv.Prod].some((env) => env === appEnv);
};

/**
 * Whether or not the app environment is the true, public facing, production environment.
 * @param appEnv current app environment
 */
export const isTrueProd = (): boolean => {
  return (
    window.location.hostname.toLowerCase() === 'dexter.spectrumtoolbox.com'
  );
};

/**
 * Whether or not the app environment is a mock based environment.
 * @param appEnv current app environment
 */
export const isMock = (appEnv: AppEnv): boolean => {
  return [AppEnv.Mock].some((env) => env === appEnv);
};

/**
 * Get the proper app env for Quantum Analytics
 * @param appEnv current app environment
 */
export const getQuantumEnvName = (appEnv: AppEnv): QuantumAppEnvNameType => {
  switch (appEnv) {
    case AppEnv.Dev:
      return 'dev';
    case AppEnv.QA:
      return 'qa';
    case AppEnv.Uat:
      return 'uat';
    case AppEnv.EngProd:
      return 'engprod';
    case AppEnv.Prod:
      return 'prod';

    default:
      return 'mock';
  }
};

/**
 * Some parts of the application won't function correctly when ran locally.
 */
export const isLocalhost = (): boolean => {
  return window.location.hostname.toLowerCase().startsWith('localhost');
};

export const getLoginUrl = (authEndpoint: string, appEnv: AppEnv): string => {
  if (appEnv === AppEnv.Mock) {
    return '/'; // Charter Blue based apps have no public facing Login page so return root and mock will handle it from there.
  }

  return `${authEndpoint}/login`;
};

export const getLoginParams = (piClientId: string, appEnv: AppEnv): string => {
  if (appEnv === AppEnv.Mock) {
    return '/'; // Charter Blue based apps have no public facing Login page so return root and mock will handle it from there.
  }

  const targetBG = getTargetBg();
  const queryParams = [
    `client_id=${piClientId}`,
    `target_bg=${targetBG}`,
    `redirect_uri=${window.location.origin.toLowerCase()}`,
    `state=state`,
  ];

  return `${queryParams.join('&')}`;
};
